// =============================================================================
// LIGA AJUPAM — Por qué un participante está bloqueado (v6.11.46)
// =============================================================================
// Una sola función pura decide si el participante entra a la app o ve la pantalla
// de bloqueo, y POR QUÉ. El "por qué" importa: la pantalla de bloqueo era una
// sola, con copy de deuda, y ahora la misma pantalla tiene que explicarle al que
// no tiene compañero algo completamente distinto.
//
// Reglas (todas sólo con la temporada en curso — antes de que arranque no se
// bloquea a nadie, que es como venía funcionando):
//
//   MANUAL      El admin bloqueó a mano el perfil o el equipo. Gana sobre todo lo
//               demás: es una decisión explícita y puede tener motivos que el
//               sistema no conoce.
//   NO_PARTNER  El equipo quedó sin compañero. Va ANTES que la deuda: si el
//               equipo ni siquiera está formado, cobrarle es la segunda
//               conversación, no la primera.
//   DEBT        Le falta pagar (impago o pago parcial).
//
// Fuera de alcance a propósito: LISTA_ESPERA y SUPLENTE no se bloquean por deuda.
// No son parte de La Liga todavía / no se les cobra la inscripción igual, así que
// mostrarles "saldá tu deuda" sería mentirles.
//
// ⚠️ ESPEJO EN EL BACKEND (v6.11.60): las reglas AUTOMÁTICAS de acá —NO_PARTNER y
// DEBT, con su prioridad, la dispensa `auto_block_exempt` y la puerta de
// temporada— están escritas otra vez en SQL, en `_team_auto_block_reason(team_id)`.
// Hacen falta ahí porque el scheduling (can_schedule_match, get_pending_opponents,
// user_propose_challenge) sólo miraba `teams.is_blocked`, que es el candado MANUAL,
// y le dejaba proponer partidos a parejas que esta función muestra bloqueadas.
// Si cambia una regla acá, cambiarla también en la migración de la función SQL.

export type BlockReason = 'MANUAL' | 'NO_PARTNER' | 'DEBT';

export interface BlockStateInput {
  userIsBlocked?: boolean | null;
  teamIsBlocked?: boolean | null;
  /** null/undefined = el equipo todavía no cargó → no se bloquea nada. */
  teamStatus?: string | null;
  /** id del compañero; null = equipo incompleto. */
  teamPartnerId?: string | null;
  teamAmountPaid?: number | null;
  teamDiscountAmount?: number | null;
  registrationFee?: number | null;
  isSeasonActive?: boolean | null;
  isBracketPublished?: boolean | null;
  /**
   * v6.11.54 — el admin levantó el bloqueo AUTOMÁTICO para este equipo
   * (`teams.auto_block_exempt`). No alcanza con "apagarlo": deuda y falta de
   * compañero se recalculan en cada render, así que la dispensa tiene que ser
   * un dato persistido. NO afecta al bloqueo manual, que es decisión aparte.
   */
  autoBlockExempt?: boolean | null;
}

export interface BlockState {
  blocked: boolean;
  reason: BlockReason | null;
  /** Lo que falta pagar, para el copy de DEBT. 0 en cualquier otro caso. */
  remaining: number;
}

const NOT_BLOCKED: BlockState = { blocked: false, reason: null, remaining: 0 };

export function resolveBlockState(input: BlockStateInput): BlockState {
  const {
    userIsBlocked,
    teamIsBlocked,
    teamStatus,
    teamPartnerId,
    teamAmountPaid,
    teamDiscountAmount,
    registrationFee,
    isSeasonActive,
    isBracketPublished,
    autoBlockExempt,
  } = input;

  // Mismo gate que traía la app: fuera de temporada no se bloquea a nadie.
  const seasonRunning = !!isSeasonActive || !!isBracketPublished;
  if (!seasonRunning) return NOT_BLOCKED;

  // Decisión explícita del admin — gana sobre las automáticas.
  if (userIsBlocked || teamIsBlocked) {
    return { blocked: true, reason: 'MANUAL', remaining: 0 };
  }

  // Sin equipo cargado todavía no hay nada que evaluar. Importante que sea
  // "no bloqueado": durante el arranque `team` llega null y bloquear acá le
  // pintaría la pantalla roja a todo el mundo por un instante.
  if (!teamStatus) return NOT_BLOCKED;

  // Un equipo eliminado tiene su propia pantalla; no se le encima el bloqueo.
  if (teamStatus === 'ELIMINADO') return NOT_BLOCKED;

  // v6.11.54 — dispensa del admin: se salta NO_PARTNER y DEBT. Va DESPUÉS del
  // bloqueo manual a propósito: si el admin además puso el candado, ese candado
  // manda (dispensar la deuda no es lo mismo que levantar una sanción).
  if (autoBlockExempt) return NOT_BLOCKED;

  // Equipo sin compañero, con la temporada ya empezada.
  if (!teamPartnerId) {
    return { blocked: true, reason: 'NO_PARTNER', remaining: 0 };
  }

  // Deuda: sólo a los que son parte de La Liga y a los que se les cobra.
  if (teamStatus !== 'ACTIVO') return NOT_BLOCKED;

  const fee = Number(registrationFee) || 0;
  // Sin arancel configurado (o settings todavía cargando) no se deduce deuda:
  // fallar del lado de dejar entrar, nunca del de trabar a un equipo al día.
  if (fee <= 0) return NOT_BLOCKED;

  const paid = Number(teamAmountPaid) || 0;
  const discount = Number(teamDiscountAmount) || 0;
  const remaining = Math.max(0, fee - paid - discount);

  if (remaining > 0) {
    return { blocked: true, reason: 'DEBT', remaining };
  }

  return NOT_BLOCKED;
}
